// ============================================================================
// Donor self-service entry point for managing / cancelling a monthly donation.
// A button opens a dialog asking for the phone + email used when setting up the
// donation. On a match it opens the manage page (/recurring?t=<token>); if no
// active donation matches, it shows how to contact Pause & Breathe.
// ============================================================================
import { useState } from "react";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAws } from "../aws";

const WHATSAPP = "9888 7087";
const CONTACT_EMAIL = "hello@pausebreathe.org";

const ManageRecurringButton = () => {
  const navigate = useNavigate();
  const { awsHttp } = useAws();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const reset = () => {
    setPhone("");
    setEmail("");
    setNotFound(false);
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    setOpen(false);
    reset();
  };

  const handleSubmit = () => {
    if (loading) return;
    setNotFound(false);
    if (!phone.trim() || !email.trim()) {
      setNotFound(true);
      return;
    }
    setLoading(true);
    awsHttp({
      HTTPRequestMethod: "POST",
      APIFunction: "user/lookup_subscription",
      unauthorize: true,
      requestBody: { phone: phone.trim(), email: email.trim() },
    })
      .then((res) => {
        const body = res?.data?.body;
        if (body && body.found && body.manage_token) {
          const t = body.manage_token;
          // Close the dialog BEFORE navigating so MUI restores body scroll/focus.
          // Navigating with the modal still open can leave the next page inert
          // (looks "frozen" until a refresh).
          setLoading(false);
          setOpen(false);
          setTimeout(() => navigate(`/recurring?t=${t}`), 0);
        } else {
          setNotFound(true);
          setLoading(false);
        }
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  };

  return (
    <>
      <div style={{ textAlign: "center", marginTop: 8, marginBottom: 8 }}>
        <Button variant="text" size="small" onClick={() => setOpen(true)} sx={{ textTransform: "none", color: "#5b7c99" }}>
          已設立每月捐款？按此管理或取消
        </Button>
      </div>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>管理每月定期捐款</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14, mb: 2 }}>
            請輸入您設立每月捐款時所使用的電話號碼及電郵地址以核實身份。
          </DialogContentText>
          <TextField
            label="電話號碼"
            fullWidth
            variant="standard"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            sx={{ mb: 2 }}
            disabled={loading}
          />
          <TextField
            label="電郵地址"
            fullWidth
            variant="standard"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          {notFound ? (
            <Alert severity="warning" sx={{ mt: 2, fontSize: 13 }}>
              找不到相符的有效每月捐款紀錄。請確認電話及電郵是否正確，或透過 WhatsApp{" "}
              <b>{WHATSAPP}</b> 或電郵{" "}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> 聯絡「停一停 心呼吸」。
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            取消
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            {loading ? <CircularProgress size={20} color="inherit" /> : "核實並繼續"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageRecurringButton;
